import { useRef, useState, type ReactNode, type CSSProperties, type TouchEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, errorMessage } from "../../shared/api/errors.ts";
import type { components } from "../../shared/api/schema";
import { useApiClient, useSession } from "../../shared/session/SessionProvider.tsx";
import { DeviceOption } from "../../shared/devices/DeviceOption.tsx";
import { deviceLabel, deviceRuntimeQueryKey, isDeviceOnline, useActiveDevice, useDeviceRuntime, type Device } from "../../shared/devices/devices.ts";
import { BottomSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { ArrowBackIcon, ArrowForwardIcon, MoreIcon } from "../../shared/ui/icons.tsx";
import styles from "./EvaPage.module.css";
import { AngelRR } from "./AngelRR.tsx";
import { DEFAULT_PALETTE_ID, PALETTES } from "./palettes.ts";
import { DEX, dexNumber, type DexEntry } from "./dex.ts";

type Settings = components["schemas"]["DeviceSettingsRequest"];
type Runtime = components["schemas"]["DeviceRuntimeResponse"];

/** Соседняя E-va по кругу: `step` −1 — предыдущая, +1 — следующая. `null` — листать некуда. */
export function useSwitchDevice(): ((step: -1 | 1) => void) | null {
  const { devices, active, setActive } = useActiveDevice();
  if (!devices || devices.length < 2 || !active) return null;
  const index = devices.findIndex(device => device.id === active.id);
  return step => setActive(devices[(index + step + devices.length) % devices.length]!.id);
}

/** Свайп по карточке E-va листает открытки: горизонтальный жест длиннее 48 px и заметно больше вертикального. */
export function useSwipe(onSwipe: ((step: -1 | 1) => void) | null) {
  const start = useRef<{ x: number; y: number } | null>(null);
  if (!onSwipe) return {};
  return {
    onTouchStart: (event: TouchEvent) => { const touch = event.touches[0]; start.current = touch && event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null; },
    onTouchEnd: (event: TouchEvent) => {
      const from = start.current, touch = event.changedTouches[0];
      start.current = null;
      if (!from || !touch) return;
      const dx = touch.clientX - from.x, dy = touch.clientY - from.y;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) onSwipe(dx < 0 ? 1 : -1);
    },
  };
}

/** Переключатель открыток над карточкой: стрелки, название с номером и точки; тап по названию — список. */
export function DeviceSelection() {
  const { devices, active, setActive } = useActiveDevice();
  const { user } = useSession();
  const switchDevice = useSwitchDevice();
  const [open, setOpen] = useState(false);
  if (!devices || devices.length < 2 || !active || !switchDevice) return null;
  const index = devices.findIndex(device => device.id === active.id);
  return <>
    <nav className={styles.switcher} aria-label="Мои открытки">
      <button type="button" className={styles.switchArrow} aria-label="Предыдущая открытка" onClick={() => switchDevice(-1)}><ArrowBackIcon /></button>
      <button type="button" className={styles.switchCurrent} aria-label="Выбрать открытку" onClick={() => setOpen(true)}>
        <span className={styles.switchName}>{deviceLabel(active, user?.id)}</span>
        <span className={styles.switchDots} aria-hidden="true">{devices.map((device, i) => <span key={device.id} className={i === index ? styles.dotActive : styles.dot} />)}</span>
        <span className={styles.visuallyHidden}>{`${index + 1} из ${devices.length}`}</span>
      </button>
      <button type="button" className={styles.switchArrow} aria-label="Следующая открытка" onClick={() => switchDevice(1)}><ArrowForwardIcon /></button>
    </nav>
    <BottomSheet title="Мои E-va" open={open} onClose={() => setOpen(false)}>
      <ul className={styles.deviceList}>{devices.map(device => <li key={device.id}><DeviceOption device={device} aria-current={device.id === active?.id || undefined} onClick={() => { setActive(device.id); setOpen(false); }} /></li>)}</ul>
    </BottomSheet>
  </>;
}

export function DeviceControls({ device, children }: { device: Device; children: ReactNode }) {
  const client = useApiClient();
  const cache = useQueryClient();
  const key = deviceRuntimeQueryKey(device.id);
  const runtime = useDeviceRuntime(device.id);
  const [notice, setNotice] = useState("");
  const [wifiConfirm, setWifiConfirm] = useState(false);
  const settings = useMutation({ scope: { id: `controls:${device.id}` }, mutationFn: async (body: Settings) => {
    const { data, error, response } = await client.PATCH("/v1/devices/{deviceId}/settings", { params: { path: { deviceId: device.id } }, body });
    if (!data) throw ApiError.from(response, error);
    return data;
  }, onSuccess: data => { cache.setQueryData(key, data); setNotice(isDeviceOnline(device) ? "Настройки сохранены" : "Настройки сохранены. Применятся после подключения"); } });
  const action = useMutation({ mutationFn: async (value: string) => {
    const { data, error, response } = await client.POST("/v1/devices/{deviceId}/actions", { params: { path: { deviceId: device.id } }, body: { action: value } });
    if (!data) throw ApiError.from(response, error);
    return data;
  }, onSuccess: () => { setNotice("Команда отправлена на открытку"); void cache.invalidateQueries({ queryKey: key }); } });
  const value = runtime.data;
  const ready = value?.controlsSupported === true;
  const online = isDeviceOnline(device);
  const disabled = !ready || settings.isPending || action.isPending;
  const sendAction = (name: string) => { setNotice(""); action.mutate(name); };
  const failure = settings.error || action.error;
  return <>
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>Экран и звук</h2>
      {children}
      {value && <div className={styles.settingsSection}>
        <label className={styles.settingRow}><span>Звук открытки<small>Нажатия и новые сообщения</small></span><input type="checkbox" role="switch" checked={value.soundEnabled} disabled={disabled} onChange={e => settings.mutate({ soundEnabled: e.target.checked })} /></label>
        <Volume value={value.soundVolumePercent} disabled={disabled} save={soundVolumePercent => settings.mutate({ soundVolumePercent })} />
      </div>}
      {notice && <p className={styles.hint} role="status">{notice}</p>}
      {failure && <p className={styles.error} role="alert">{errorMessage(failure)}</p>}
    </section>
    {value && <Palettes value={value} disabled={disabled} save={paletteId => settings.mutate({ paletteId })} />}
    {value?.karaokeSupported && <AngelRR key={device.id} deviceId={device.id} />}
    {value && <Pomodoro value={value} disabled={disabled} online={online} save={body => settings.mutate(body)} action={sendAction} />}
    <section className={styles.card}>
      <div className={styles.brightnessHeader}><h2 className={styles.cardTitle}>Подключение</h2><span className={styles.hint}>Wi-Fi · 2,4 ГГц</span></div>
      <div className={styles.networkRow}><div><p className={styles.settingValue}>{value?.wifiSsid || (runtime.isPending ? "Проверяю сеть…" : "Сеть пока неизвестна")}</p><p className={styles.hint}>{online ? "Открытка в сети" : "Последняя известная сеть"}</p></div><span className={online ? styles.signalOnline : styles.signalOffline}>{online ? "На связи" : "Нет связи"}</span></div>
      {runtime.error && <p className={styles.error} role="alert">{errorMessage(runtime.error)}</p>}
      {!ready && value && <p className={styles.hint}>Для управления нужно обновление прошивки</p>}
      <Button variant="secondary" disabled={disabled || !online} onClick={() => setWifiConfirm(true)}>Сменить Wi-Fi</Button>
    </section>
    <BottomSheet title="Сменить Wi-Fi" open={wifiConfirm} onClose={() => setWifiConfirm(false)}>
      <p className={styles.hint}>На открытке появится подтверждение: удерживайте кнопку 5 секунд, чтобы сменить Wi-Fi; короткое нажатие отменяет действие. После подтверждения открытка покажет QR настройки Wi-Fi. Подключитесь к EVA-SETUP с паролем eva-setup, откройте 192.168.4.1 и укажите новую сеть 2,4 ГГц. Привязка к аккаунту сохранится.</p>
      <Button disabled={action.isPending} onClick={() => { setWifiConfirm(false); sendAction("wifi.setup"); }}>Перейти к настройке Wi-Fi</Button>
    </BottomSheet>
  </>;
}

function Volume({ value, disabled, save }: { value: number; disabled: boolean; save: (volume: number) => void }) {
  const [changed, setChanged] = useState<number | null>(null);
  const draft = changed ?? value;
  return <label className={styles.volume}>Громкость · {draft}%<input className={styles.slider} style={{ "--fill": draft / 100 } as CSSProperties} aria-label="Громкость" type="range" min={0} max={100} value={draft} disabled={disabled} onChange={e => setChanged(Number(e.target.value))} onPointerUp={() => { if (draft !== value) save(draft); setChanged(null); }} onKeyUp={() => { if (draft !== value) save(draft); setChanged(null); }} /></label>;
}

/** Три палитры на табе E-va и «⋯» — покедекс со всеми записями. Эксклюзивы открывает NFC-метка на E-va. */
function Palettes({ value, disabled, save }: { value: Runtime; disabled: boolean; save: (paletteId: string) => void }) {
  const applied = PALETTES.find(palette => palette.id === value.paletteId) ?? PALETTES.find(palette => palette.id === DEFAULT_PALETTE_ID)!;
  const open = new Set(value.unlockedPaletteIds);
  const [dexOpen, setDexOpen] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  // Стоящая палитра первой, за ней две следующие по номеру.
  const music = DEX.filter(entry => !entry.exclusive);
  const start = Math.max(0, music.findIndex(entry => entry.id === applied.id));
  const featured = [0, 1, 2].map(offset => music[(start + offset) % music.length]!);
  const choose = (entry: DexEntry) => {
    if (!open.has(entry.id)) return setHint("Запись скрыта. Поднеси NFC-метку к E‑va, чтобы открыть её.");
    if (entry.exclusive) return setHint(`${entry.name} надевается меткой прямо на E‑va.`);
    setHint(null);
    setDexOpen(false);
    if (entry.id !== applied.id) save(entry.id);
  };
  const sprite = (entry: DexEntry, known = true) => <span className={styles.dexSprite} style={known ? { "--palette-bg": entry.bg, "--palette-text": entry.text, "--palette-accent": entry.accent } as CSSProperties : undefined} aria-hidden="true" />;
  return <section className={styles.card}>
    <div className={styles.brightnessHeader}><h2 className={styles.cardTitle}>Оформление</h2>
      {value.palettesSupported && <button type="button" className={styles.dexMore} aria-label="Все оформления" onClick={() => { setHint(null); setDexOpen(true); }}><MoreIcon /></button>}
    </div>
    {value.palettesSupported
      ? <div className={styles.dexFeatured} role="radiogroup" aria-label="Палитра открытки">
        {featured.map(entry => <button key={entry.id} type="button" role="radio" aria-checked={entry.id === applied.id} aria-label={entry.name} className={styles.dexTile} disabled={disabled} onClick={() => choose(entry)}>
          {sprite(entry)}<span className={styles.dexTileName}>{entry.name}</span>
        </button>)}
      </div>
      : <p className={styles.hint}>Палитры появятся после обновления прошивки</p>}
    <BottomSheet title="Покедекс" open={dexOpen} onClose={() => setDexOpen(false)}>
      <p className={styles.hint}>Собрано {DEX.filter(entry => open.has(entry.id)).length} / {DEX.length}</p>
      <ol className={styles.dexList} aria-label="Все оформления">
        {DEX.map(entry => {
          const known = open.has(entry.id);
          const isApplied = known && entry.id === applied.id;
          return <li key={entry.id}>
            <button type="button" aria-pressed={isApplied} aria-label={known ? entry.name : `${dexNumber(entry)} скрыта`} className={styles.dexRow} data-locked={!known || undefined} disabled={disabled} onClick={() => choose(entry)}>
              <span className={styles.dexNumber}>{dexNumber(entry)}</span>
              {sprite(entry, known)}
              <span className={styles.dexName}>{known ? entry.name : "???"}</span>
              {isApplied && <span className={styles.dexTag}>На E‑va</span>}
              {known && entry.exclusive && <span className={styles.dexTag}>Метка</span>}
            </button>
          </li>;
        })}
      </ol>
      <p className={styles.hint} aria-live="polite">{hint ?? "Эксклюзивы открываются NFC-меткой: поднеси её к E‑va."}</p>
    </BottomSheet>
  </section>;
}

const TIMER_KINDS = [["pomodoro", "Помодоро"], ["stopwatch", "Секундомер"], ["countdown", "Таймер"]] as const;

function formatSeconds(total: number) {
  return `${Math.floor(total / 60).toString().padStart(2, "0")}:${(total % 60).toString().padStart(2, "0")}`;
}

function Pomodoro({ value, disabled, online, save, action }: { value: Runtime; disabled: boolean; online: boolean; save: (body: Settings) => void; action: (name: string) => void }) {
  const [draft, setDraft] = useState<Settings | null>(null);
  const work = draft?.workMinutes ?? value.workMinutes;
  const short = draft?.shortBreakMinutes ?? value.shortBreakMinutes;
  const long = draft?.longBreakMinutes ?? value.longBreakMinutes;
  const cycles = draft?.cyclesBeforeLongBreak ?? value.cyclesBeforeLongBreak;
  const dirty = draft !== null;
  const pomo = value.pomodoro;
  const modes = value.timerModesSupported;
  const kind = modes ? value.timerKind : "pomodoro";
  const [minutes, setMinutes] = useState<number | null>(null);
  const countdownMinutes = minutes ?? value.countdownMinutes;
  const seconds = kind === "stopwatch" ? pomo.elapsedSec ?? 0 : pomo.remainingSec;
  const running = pomo.state === "running";
  const phases: Record<string, string> = { work: "Фокус", short_break: "Короткий перерыв", long_break: "Большой перерыв" };
  const caption = kind === "stopwatch" ? "Секундомер"
    : kind === "countdown" ? (pomo.alarm ? "Время вышло" : `Таймер · из ${formatSeconds(pomo.durationSec ?? value.countdownMinutes * 60)}`)
    : `${phases[pomo.phase] || pomo.phase} · цикл ${pomo.currentCycle}`;
  return <section className={styles.card}>
    <div className={styles.brightnessHeader}><h2 className={styles.cardTitle}>{modes ? "Таймеры" : "Pomodoro"}</h2><span className={styles.hint}>{running ? "Идёт" : pomo.state === "paused" ? "Пауза" : "Готов"}</span></div>
    {modes && <div className={styles.modeSwitch} role="radiogroup" aria-label="Режим таймера">
      {TIMER_KINDS.map(([id, label]) => <button key={id} type="button" role="radio" aria-checked={id === kind} disabled={disabled} onClick={() => { if (id !== kind) save({ timerKind: id }); }}>{label}</button>)}
    </div>}
    <p className={styles.timer}>{formatSeconds(seconds)}</p>
    <p className={styles.hint}>{caption}{!online ? " · последнее состояние" : ""}</p>
    <div className={styles.controlButtons}><Button disabled={disabled || !online} onClick={() => action(running ? "pomodoro.pause" : "pomodoro.start")}>{running ? "Пауза" : "Старт"}</Button><Button variant="secondary" disabled={disabled || !online} onClick={() => action("pomodoro.reset")}>Сброс</Button></div>
    <Button variant="secondary" disabled={disabled || !online} onClick={() => action("clock")}>Показать часы</Button>
    {kind === "countdown" && <div className={styles.countdownRow}>
      <label>Минут<input aria-label="Минут" type="number" min={1} max={99} value={countdownMinutes} disabled={disabled} onChange={e => setMinutes(Number(e.target.value))} /></label>
      <Button variant="secondary" disabled={disabled || minutes === null || countdownMinutes < 1 || countdownMinutes > 99 || countdownMinutes === value.countdownMinutes} onClick={() => { save({ countdownMinutes }); setMinutes(null); }}>Сохранить время</Button>
    </div>}
    {kind === "pomodoro" && <details className={styles.intervals}><summary>Настроить интервалы</summary><div className={styles.settingsGrid}>
      {([ ["Фокус, мин", work, "workMinutes", 60], ["Перерыв, мин", short, "shortBreakMinutes", 30], ["Большой, мин", long, "longBreakMinutes", 60], ["Циклов", cycles, "cyclesBeforeLongBreak", 10] ] as const).map(([label, number, field, max]) => <label key={label}>{label}<input aria-label={label} type="number" min={1} max={max} value={number} disabled={disabled} onChange={e => { setDraft(current => ({ ...current, [field]: Number(e.target.value) })); }} /></label>)}
    </div>
    <Button variant="secondary" disabled={disabled || !dirty || work < 1 || work > 60 || short < 1 || short > 30 || long < 1 || long > 60 || cycles < 1 || cycles > 10} onClick={() => { if (draft) save(draft); setDraft(null); }}>Сохранить интервалы</Button></details>}
  </section>;
}
